import React, { useState } from 'react';
import { usePageMeta } from '../components/ui';
import { adProvider, adsEnabled, contactEmail, donationUrl, openConsentChoices } from '../monetization';
import { FAN_CONTENT_NOTICE, FAN_CONTENT_POLICY_URL } from '../components/SiteFooter';

/* Written to match what the build actually turns on (analytics.js and
   monetization.js), so the page never claims cookies the site doesn't set
   or hides ones it does. */
const analytics = (process.env.REACT_APP_ANALYTICS_PROVIDER || 'cloudflare').toLowerCase();
const analyticsOn =
  (analytics === 'cloudflare' && process.env.REACT_APP_CF_ANALYTICS_TOKEN) ||
  (analytics === 'plausible' && process.env.REACT_APP_PLAUSIBLE_DOMAIN);
const analyticsName = analytics === 'plausible' ? 'Plausible Analytics' : 'Cloudflare Web Analytics';

function Privacy() {
  const [noChoices, setNoChoices] = useState(false);
  usePageMeta({
    title: 'Privacy & Cookies',
    description: 'What StatYoyo collects, which cookies its ads use, and how to change your choices.',
  });

  return (
    <div className="page">
      <div className="wrap prose">
        <div className="phead">
          <div>
            <div className="eyebrow">StatYoyo</div>
            <h1>Privacy &amp; cookies</h1>
          </div>
        </div>

        <h2>The short version</h2>
        <p>
          StatYoyo has no accounts and no sign-up. Every feature is free for everyone. We don't sell
          personal data.
        </p>

        <h2>What we handle</h2>
        <ul>
          <li>
            <b>Player tags you look up.</b> They're sent to our server and on to the official game APIs to fetch
            public profile and battle data. Public game data we've collected is shown to everyone.
          </li>
          <li>
            <b>Server logs.</b> Like any website, our servers and our network provider (Cloudflare) see your IP
            address and the pages you request. We use this only to keep the site running and to stop abuse.
          </li>
          {analyticsOn && (
            <li>
              <b>Visit counts.</b> We use {analyticsName} to count page views. It doesn't set cookies and doesn't
              build a profile of you.
            </li>
          )}
        </ul>

        <h2 id="ads">Ads and cookies</h2>
        {adsEnabled ? (
          <>
            <p>
              Ads help pay for the servers. They're served by {adProvider.label}, which, along with its partners,
              may use cookies or similar technologies to show ads, limit how often you see them, measure them and,
              where you allow it, personalise them based on your visits to this and other sites.
              StatYoyo itself doesn't set any advertising cookies.
            </p>
            <p>
              In the UK, EEA and Switzerland you're asked for consent before personalised ads are shown, and you
              can change your answer any time:{' '}
              <button
                type="button"
                className="linklike"
                onClick={() => setNoChoices(!openConsentChoices())}
              >
                Cookie choices
              </button>.
              {noChoices && (
                <span className="muted"> The consent tool isn't loaded here, which usually means no choices apply in your region.</span>
              )}
            </p>
            <p>
              Read how {adProvider.label} uses data from sites like this{' '}
              <a href={adProvider.privacyUrl} target="_blank" rel="noopener noreferrer">here</a>, and turn off
              personalised ads at <a href={adProvider.optOutUrl} target="_blank" rel="noopener noreferrer">{adProvider.optOutUrl.replace('https://', '')}</a>.
            </p>
          </>
        ) : (
          <p>StatYoyo doesn't show ads at the moment and sets no cookies.</p>
        )}

        {donationUrl && (
          <>
            <h2>Donations</h2>
            <p>
              Donations are handled by the payment page we link to, under its own privacy policy. They're optional
              and don't unlock anything: the site is the same for everyone.
            </p>
          </>
        )}

        {contactEmail && (
          <>
            <h2>Contact</h2>
            <p>Questions about your data: <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.</p>
          </>
        )}

        <h2>Fan content</h2>
        <p>
          {FAN_CONTENT_NOTICE}{' '}
          <a href={FAN_CONTENT_POLICY_URL} target="_blank" rel="noopener noreferrer">www.supercell.com/fan-content-policy</a>.
        </p>
      </div>
    </div>
  );
}

export default Privacy;
